import React, { useState, useEffect } from 'react';
import { StargazeWorldItem, UniverseSectionConfig } from '../../types';
import { INITIAL_STARGAZE_WORLDS } from '../../data/initialCmsData';
import { getSiteContent } from '../../lib/cmsService';
import { StargazeImage } from '../common/StargazeImage';
import { Sparkles } from 'lucide-react';

interface StargazeUniverse3DProps {
  initialConfig?: UniverseSectionConfig;
}

export const StargazeUniverse3D: React.FC<StargazeUniverse3DProps> = ({ initialConfig }) => {
  const [universeConfig, setUniverseConfig] = useState<UniverseSectionConfig>(
    initialConfig || {
      eyebrow: '03 // STUDIO DIVISIONS',
      heading: 'DIVISIONS & SERVICES',
      description: 'Explore our six primary cinema divisions powering entertainment from development to global release.',
      worlds: INITIAL_STARGAZE_WORLDS,
    }
  );

  useEffect(() => {
    let isMounted = true;
    async function loadUniverse() {
      try {
        const siteData = await getSiteContent();
        if (isMounted && siteData?.universeSection?.worlds && siteData.universeSection.worlds.length > 0) {
          setUniverseConfig(siteData.universeSection);
        }
      } catch (err) {
        console.warn('Could not load dynamic universe CMS data, using initial:', err);
      }
    }

    if (!initialConfig) {
      loadUniverse();
    }
    return () => {
      isMounted = false;
    };
  }, [initialConfig]);

  // Filter published & visible worlds, sort by displayOrder
  const rawWorlds = universeConfig.worlds && universeConfig.worlds.length > 0
    ? universeConfig.worlds
    : INITIAL_STARGAZE_WORLDS;

  const worlds = rawWorlds
    .filter((w) => w.status !== 'DRAFT' && w.visible !== false)
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  return (
    <section className="py-24 bg-white border-t border-zinc-200/80 relative overflow-hidden" id="categories">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[#B45309] font-mono text-xs font-bold tracking-widest uppercase">
            <Sparkles className="w-3.5 h-3.5 fill-[#D97706] text-[#D97706]" />
            {universeConfig.eyebrow || '03 // STUDIO DIVISIONS'}
          </div>
          <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic tracking-tight uppercase text-zinc-900">
            {universeConfig.heading || 'DIVISIONS & SERVICES'}
          </h2>
          <p className="text-zinc-600 font-sans text-sm sm:text-base leading-relaxed">
            {universeConfig.description || 'Explore our six primary cinema divisions powering entertainment from development to global release.'}
          </p>
        </div>

        {/* 6 Category Cards in Balanced 3-Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {worlds.map((world, idx) => {
            const buttonText = world.buttonLabel?.trim() || world.name?.trim() || `CATEGORY ${idx + 1}`;
            const targetUrl = world.link || '/';

            return (
              <a
                key={world.id || idx}
                href={targetUrl}
                data-cursor="VIEW"
                aria-label={`Explore ${buttonText}`}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] border border-zinc-200 bg-zinc-950 transition-all duration-500 shadow-sm hover:border-[#D97706] hover:shadow-2xl hover:shadow-amber-500/10 block focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              >
                {/* Large Visually Engaging Image Area */}
                <div className="absolute inset-0 overflow-hidden bg-zinc-950">
                  {world.imageUrl ? (
                    <StargazeImage
                      src={world.imageUrl}
                      alt={world.name || buttonText}
                      focalPoint={world.focalPoint}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-900 flex items-center justify-center text-zinc-600" />
                  )}
                  {/* Restrained subtle dark gradient overlay for optimal contrast without obscuring imagery */}
                  <div className="absolute inset-0 bg-black/35 group-hover:bg-black/20 transition-colors duration-500" />
                </div>

                {/* Visually Centred Action Button */}
                <div className="absolute inset-0 flex items-center justify-center p-6 z-10 pointer-events-none">
                  <span className="inline-flex items-center justify-center px-7 sm:px-9 py-3 sm:py-3.5 rounded-full bg-black/65 backdrop-blur-md border border-white/30 text-white font-mono font-black text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 shadow-2xl group-hover:bg-[#D97706] group-hover:text-black group-hover:border-[#D97706] group-hover:scale-105 group-hover:shadow-amber-500/25 text-center">
                    {buttonText}
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
